import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit2, Trash2, Plus, Search, Tag } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';

function FeaturesList() {
  const [features, setFeatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('features')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });
      
      if (error) {
        console.error('Error fetching features:', error);
        toast.error('Errore nel caricamento delle dotazioni');
      } else {
        setFeatures(data || []);
      }
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Errore nel caricamento delle dotazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa dotazione?')) return;
    
    try {
      // Check if the feature is being used in any property
      const { count, error: countError } = await supabase
        .from('property_features')
        .select('*', { count: 'exact', head: true })
        .eq('feature_id', id);
      
      if (countError) {
        throw countError;
      }
      
      if (count && count > 0) {
        toast.warning(`Questa dotazione è utilizzata in ${count} immobili. Rimuovi prima l'associazione.`);
        return;
      }
      
      const { error } = await supabase
        .from('features')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw error;
      }
      
      toast.success('Dotazione eliminata con successo');
      setFeatures(features.filter(feature => feature.id !== id));
    } catch (error: any) {
      console.error('Error deleting feature:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante l\'eliminazione'}`);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredFeatures = searchQuery
    ? features.filter(feature => 
        feature.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (feature.category && feature.category.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : features;

  // Group features by category
  const groupedFeatures = filteredFeatures.reduce((acc, feature) => {
    const category = feature.category || 'Altro';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(feature);
    return acc;
  }, {} as { [key: string]: any[] });

  return (
    <AdminLayout title="Gestione Dotazioni">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl font-semibold">Dotazioni</h2>
        <Link
          to="/admin/features/new"
          className="bg-primary hover:bg-primary-light text-white px-4 py-2.5 rounded-md transition-colors flex items-center justify-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuova Dotazione
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Cerca dotazione..."
            className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Caricamento dotazioni...</p>
        </div>
      ) : filteredFeatures.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna dotazione trovata</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery 
              ? 'Nessuna dotazione corrisponde alla tua ricerca.' 
              : 'Non ci sono ancora dotazioni. Crea la tua prima dotazione.'}
          </p>
          <Link
            to="/admin/features/new"
            className="inline-flex items-center bg-primary hover:bg-primary-light text-white px-4 py-2 rounded-md transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuova Dotazione
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedFeatures).map(([category, features]) => (
            <div key={category} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b">
                <h3 className="font-semibold text-gray-800">{category}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-sm uppercase">
                      <th className="py-3 px-6">Nome</th>
                      <th className="py-3 px-6">Icona</th>
                      <th className="py-3 px-6 text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map((feature) => (
                      <tr key={feature.id} className="border-t hover:bg-gray-50">
                        <td className="py-4 px-6">
                          <div className="font-medium">{feature.name}</div>
                        </td>
                        <td className="py-4 px-6">
                          {feature.icon ? feature.icon : '-'}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex justify-end space-x-2">
                            <Link
                              to={`/admin/features/${feature.id}/edit`}
                              className="text-blue-600 hover:text-blue-700"
                              title="Modifica"
                            >
                              <Edit2 className="w-5 h-5" />
                            </Link>
                            <button
                              onClick={() => handleDelete(feature.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Elimina"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

export default FeaturesList;